import { ChangeDetectionStrategy, Component, computed, effect, inject, ViewChild, viewChildren } from '@angular/core';
import { FilterInput } from '@it2go/data-grid';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FolderMedicationsPageStore } from './folder-medications-page.store';
import { FolderMedicationGridItemObject } from '@it2go/types';
import { fb, globalStore, isAllowed, translate } from '../../../../shared/shared.global-service';
import { Router } from '@angular/router';
import { DynamicDialogRef } from '@openng/optimus-ui/dynamicdialog';
import {
    BeforePrintDialogComponent,
    BeforePrintDialogResponse,
} from '../../../components/common/before-print-dialog/before-print-dialog.component';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import {
    FolderMedicationUpsertDialogComponent,
} from '../../../../../components/medical-service/folder/folder-medications/folder-medication-upsert-dialog/folder-medication-upsert-dialog.component';
import { selectCurrentService } from '../../../store/person/person.selectors';
import {
    FolderMedicationExportInput,
} from '@libs/types/src/services/input-type/folder/folder-medication/folder-medication.export-input.ts';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { formatDosing } from './folder-medications.pipe';
import { changeSignal } from '../../../../../utils/form.utils';
import { generateFilename } from '../../../../../utils/files.utils';
import { MedicationInfoComponent } from '../../../../shared/components/kit/medication-info/medication-info.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { SwitchInputComponent } from '../../../../../components/kit/input/switch-input/switch-input.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { NgClass } from '@angular/common';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { TableModule } from '@openng/optimus-ui/table';
import { countDaysDiff, CountDaysDiffPipe } from '../../../../shared/pipes/count-days-diff.pipe';
import { formatDaysDiff, FormatDaysDiffPipe } from '../../../../shared/pipes/format-days-diff.pipe';
import { getMedicationsFrag } from './folder-medications-page.query';
import { userChipField } from '../../../../shared/components/kit/table/table.fields';
import { UserChipRenderTypeEnum } from '../../../../../components/kit/user-chip/user-chip.component.types';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';
import { FolderMedicationsWorksComponent } from './folder-medications-works/folder-medications-works.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';

@Component({
    selector: 'app-folder-medications-page',
    templateUrl: './folder-medications-page.component.html',
    styleUrls: ['./folder-medications-page.component.scss'],
    providers: [FolderMedicationsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        ButtonComponent,
        TableComponent,
        PrimeTemplate,
        NgClass,
        Tooltip,
        TranslatePipe,
        MedicationInfoComponent,
        TableModule,
        CountDaysDiffPipe,
        FormatDaysDiffPipe,
        FolderMedicationsWorksComponent,
    ],
})
export class FolderMedicationsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(FolderMedicationsPageStore);
    protected readonly router = inject(Router);
    private readonly dialogService = inject(BaseDialogService);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly backgroundTaskLauncher = inject(BackgroundTaskLaunchService);

    @ViewChild('medicationTable') table!: TableComponent;

    childRef: DynamicDialogRef | undefined;

    now = new Date().toISOString();
    visible = false;

    controlsForm = fb.group({
        active: fb.control<boolean>(false),
    });

    columns: TableColumn[] = [];

    protected medInfos = viewChildren(MedicationInfoComponent);
    protected activeOnlyChange = changeSignal(
        this.controlsForm.controls.active,
    );
    protected extraFilters = computed<FilterInput[][]>(() => {
        const filter = new FilterBuilder();
        const folderId = this.store.folderId() || 0;

        if (folderId) {
            const sharingIds = this.store
                .medicationSharingIds()!
                .map((it) => String(it));
            filter.where('folderId', [String(folderId), ...sharingIds], 'IN');
        }

        if (!this.activeOnlyChange()) {
            filter.active();
        }

        return filter.filter().filter || [];
    });

    constructor() {
        super();
        this.store.onSuccess(
            () => this.table.reload(),
            'createFolderMedication',
            'updateFolderMedication',
            'deleteFolderMedication',
        );

        effect(() => {
            const columns: TableColumn[] = [
                {
                    name: 'active',
                    dataKey: null,
                    exportAs: (item: any) =>
                        translate.instant(
                            item.active
                                ? item.activeOutOfDate
                                    ? 'outOfDate'
                                    : 'yes'
                                : 'no',
                        ),
                },
            ];
            if (
                this.store.medicationSettings()?.showColumnTimesAdministrated ??
                true
            ) {
                columns.push({
                    name: 'timesAdministered',
                    data: (item: any) => `${item || 0}x`,
                });
            }
            columns.push(
                {
                    name: 'medicationCode',
                    exportAs: (code: string) => {
                        const info = this.medInfos()?.find(
                            (it) => it.medicationCode === code,
                        );

                        return info?.label() || code;
                    },
                },
                {
                    name: 'dosing',
                    dataKey: null,
                    data: (item: any) =>
                        formatDosing(
                            item.dosingPeriod,
                            item.dosing,
                            this.store.service()?.timeRanges,
                        ),
                },
                {
                    name: 'stock',
                    dataKey: null,
                    exportAs: (item: any) =>
                        formatDaysDiff(countDaysDiff(item)),
                },
                userChipField('prescribedBy', UserChipRenderTypeEnum.User, {
                    filterable: false,
                }),
            );
            this.columns = columns;
        });
    }

    protected async detail(item?: any): Promise<void> {
        this.dialogService.open(FolderMedicationUpsertDialogComponent, {
            header: translate.instant(
                `folderMedication.upsertDialog.title${item ? 'Update' : 'Create'}`,
            ),
            styleClass: 'p-dialog-width-70',
            data: {
                folderId: this.store.folderId(),
                personId: this.store.folder()?.person?.id || 0,
                folderMedicationId: item?.id || null,
                service: globalStore!.selectSignal(selectCurrentService)(),
            },
        });
    }

    async delete(item: any) {
        this.confirm.confirm(
            'common.delete',
            item.item.name,
            () => this.store.api.deleteFolderMedication({ id: item.item.id }),
            null,
            'pi-trash',
            item.event.target,
        );
    }

    changeState(item: FolderMedicationGridItemObject): void {
        this.store.api.updateFolderMedication({
            id: item.id,
            active: !item.active,
        });
    }

    printMedication(forClient: boolean = false) {
        this.childRef = this.dialogService.open(BeforePrintDialogComponent, {
            header: translate.instant('common.export'),
        });
        this.childRef.onClose.subscribe(
            async (data: BeforePrintDialogResponse) => {
                if (data) {
                    const exportInput: FolderMedicationExportInput = {
                        filename: generateFilename(
                            forClient
                                ? 'folderMedication.printForClient'
                                : 'folderMedication.labelMulti',
                        ),
                        folderId: this.store.folderId() || null,
                        forClient,
                        ...data,
                    };

                    await this.backgroundTaskLauncher.start({
                        type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                        payload: {
                            folderId: this.store.folderId() || 0,
                            kind: ClientDocumentExportKind.MEDICATION,
                            input: exportInput,
                        },
                        alreadyRunningMessage:
                            'person.documentExport.backgroundTask.messages.alreadyRunning',
                        failedMessage:
                            'person.documentExport.backgroundTask.messages.startFailed',
                    });
                }
            },
        );
    }

    protected readonly isAllowed = isAllowed;
    protected readonly getMedicationsFrag = getMedicationsFrag;
}
